import classNames from 'classnames';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
import { workspaceQuoteLoaded } from 'state/workspaces/workspace.actions';
import styled from 'styled-components';

export interface BatchExternalQuoteIdLinkProps {
  externalQuoteId: string;
  quoteId?: string;

  className?: string;
  linkClassName?: string;
  iconClassName?: string;
  targetWorkspace?: QuoteWorkspaceType;
  disabled?: boolean;
  tabIndex?: number;
}

function getRootUrl() {
  return window.location.origin + window.location.pathname;
}

export const BatchQuoteRepriceLink: FC<BatchExternalQuoteIdLinkProps> = ({
  quoteId,
  externalQuoteId,
  targetWorkspace,
  className,
  linkClassName,
  iconClassName,
  disabled,
  tabIndex,
}) => {
  const { formatMessage } = useIntl();

  const generateUrl = useCallback(() => `${getRootUrl()}#/quote/${externalQuoteId}`, [externalQuoteId]);

  const copyLinkToClipboard = useCopyTextToClipboard(
    'moreMenu.copyToClipboard.repriceLink.success',
    'moreMenu.copyToClipboard.repriceLink.error',
  );

  const dispatch = useDispatch();

  const onLinkClicked = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      dispatch(workspaceQuoteLoaded(externalQuoteId, quoteId, targetWorkspace));
      e.preventDefault();
      e.stopPropagation();
      return false;
    },
    [dispatch, externalQuoteId, quoteId, targetWorkspace],
  );

  const tooltip = useMemo(() => formatMessage({ id: 'moreMenu.copyToClipboard.repriceLink.tooltip' }), [formatMessage]);

  return (
    <StyledDiv className={className}>
      {!disabled && (
        <a
          href={generateUrl()}
          className={classNames('text-link-secondary', linkClassName)}
          data-e2e="quote-reload-url"
          onClick={onLinkClicked}
        >
          {externalQuoteId}
        </a>
      )}
      {disabled && <span className="text-secondary">{externalQuoteId}</span>}
      <button
        id="btnCopyUrl"
        type="button"
        className="btn btn-flat-secondary btn-sm ms-3"
        title={tooltip}
        onClick={() => copyLinkToClipboard(generateUrl())}
        tabIndex={tabIndex}
      >
        <i className={classNames('icon icon-xs', iconClassName)}>file_copy</i>
      </button>
    </StyledDiv>
  );
};

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 6px 10px;
`;
